import type { ReactNode } from 'react'
import { Box, ButtonBase, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined'
import { palette } from '../../ui/theme/tokens.js'
import { Sparkline } from '../../ui/charts/Sparkline.js'

/**
 * Les briques du tableau de bord.
 *
 * ⭐ Une seule forme de carte pour toute la page — même arrondi, même ombre,
 * même entête. C'est ce qui fait qu'un écran de douze blocs se lit comme un
 * tout et non comme douze composants posés côte à côte.
 */

/** Un chiffre d'entête de carte : ce que le graphe dessous permet de lire. */
export interface ChiffreEntete {
  /** Son libellé, AVEC sa portée : « Courantes · aujourd'hui ». */
  libelle: string
  valeur: string
  /** La pastille de la série — elle tient lieu de légende au graphe. */
  couleur?: string
  /** Une précision sous le chiffre : ce que le chiffre compte, au juste. */
  aide?: string
}

/** Carte du tableau de bord : titre, sous-titre, action à droite, contenu. */
export function CarteTableau({
  titre,
  sousTitre,
  action,
  chiffres,
  children,
  sx,
}: {
  titre: string
  sousTitre?: string
  /** À droite de l'entête : un lien « Voir… », un bouton graphe. */
  action?: ReactNode
  /**
   * ⭐ Les CHIFFRES du graphe, dans l'entête : on lit le montant avant la
   * forme, sans avoir à survoler une colonne. Leurs pastilles servent de
   * légende — le graphe n'en répète pas une seconde.
   */
  chiffres?: readonly ChiffreEntete[]
  children: ReactNode
  sx?: object
}): JSX.Element {
  return (
    <Paper
      component="section"
      aria-label={titre}
      sx={{
        p: { xs: 4, md: 5 },
        borderRadius: 2,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        // ⭐ Une ombre douce plutôt qu'un cadre : la carte se détache du fond
        // sans l'encre d'un trait. En sombre, où l'ombre ne se voit pas, le
        // filet du thème reprend son rôle.
        borderColor: (t) =>
          t.palette.mode === 'dark' ? palette.neutral[800] : palette.neutral[100],
        boxShadow: (t) =>
          t.palette.mode === 'dark'
            ? 'none'
            : '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, lineHeight: '24px' }}>
            {titre}
          </Typography>
          {sousTitre ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {sousTitre}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      {chiffres && chiffres.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${chiffres.length}, minmax(0, 1fr))`,
            gap: 3,
            mt: -1,
            mb: 3,
          }}
        >
          {chiffres.map((c) => (
            <Box key={c.libelle} sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {c.couleur ? (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      bgcolor: c.couleur,
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                  noWrap
                  title={c.libelle}
                >
                  {c.libelle}
                </Typography>
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: 20, lineHeight: '28px' }}
                noWrap
                title={c.valeur}
              >
                {c.valeur}
              </Typography>
              {c.aide ? (
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                  {c.aide}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Paper>
  )
}

/** Lien d'entête « Voir… → », vers la liste qui explique le chiffre. */
export function LienVoir({
  libelle,
  onClick,
}: {
  libelle: string
  onClick: () => void
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        color: 'primary.main',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
        '&:hover': { bgcolor: 'action.hover' },
        '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main' },
      }}
    >
      {libelle}
      <ArrowForwardIcon sx={{ fontSize: 14 }} />
    </ButtonBase>
  )
}

/** Bouton « Voir le graphe » d'une carte. */
export function BoutonGraphe({
  libelle,
  onClick,
}: {
  libelle: string
  onClick: () => void
}): JSX.Element {
  return (
    <Tooltip title="Voir le graphe">
      <IconButton
        size="small"
        aria-label={`Voir le graphe : ${libelle}`}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        sx={{ color: 'text.secondary', position: 'relative', zIndex: 2, flexShrink: 0 }}
      >
        <InsertChartIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

/**
 * Variation face à la période comparable : flèche, signe et pourcentage.
 *
 * ⚠️ La couleur dit si c'est une BONNE nouvelle, pas seulement le sens :
 * davantage de ventes est vert, davantage de dépenses serait rouge. Et elle
 * ne parle jamais seule — la flèche et le signe portent l'information.
 */
export function Variation({
  valeur,
  hausseFavorable = true,
  surFondFonce = false,
  libelle,
}: {
  valeur: number | null
  hausseFavorable?: boolean
  /** Sur la carte vedette : pastille claire sur fond bleu. */
  surFondFonce?: boolean
  /** Ce à quoi on compare : « vs mois dernier ». */
  libelle?: string
}): JSX.Element | null {
  if (valeur === null) return null
  const hausse = valeur >= 0
  const bonne = hausse === hausseFavorable
  const couleur = surFondFonce ? '#FFFFFF' : bonne ? palette.success[700] : palette.danger[700]
  const fond = surFondFonce
    ? 'rgba(255,255,255,0.18)'
    : bonne
      ? palette.success[50]
      : palette.danger[50]
  const Fleche = hausse ? ArrowUpwardIcon : ArrowDownwardIcon
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          px: 1,
          py: 0.25,
          borderRadius: 5,
          bgcolor: fond,
          color: couleur,
        }}
      >
        <Fleche sx={{ fontSize: 13 }} />
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: 'inherit' }}>
          {hausse ? '+' : '−'}
          {Math.abs(valeur).toLocaleString('fr-FR')} %
        </Typography>
      </Box>
      {libelle ? (
        <Typography
          component="span"
          variant="caption"
          sx={{ color: surFondFonce ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}
        >
          {libelle}
        </Typography>
      ) : null}
    </Box>
  )
}

/**
 * Carte d'INDICATEUR : le chiffre, sa variation, sa tendance.
 *
 * ⭐ Toute la carte mène à la liste qui explique le chiffre ; le bouton graphe,
 * FRÈRE et non enfant du bouton de carte, ouvre le détail. Un bouton dans un
 * bouton est invalide, et déclenchait les deux clics.
 */
export function CarteIndicateur({
  libelle,
  valeur,
  unite,
  variation,
  libelleVariation,
  hausseFavorable = true,
  tendance,
  avertissement,
  note,
  onClick,
  onGraphe,
}: {
  libelle: string
  valeur: string
  unite?: string
  variation?: number | null
  libelleVariation?: string
  hausseFavorable?: boolean
  tendance?: readonly (number | null)[]
  /** Une ligne d'alerte sous le chiffre (stock, par exemple). */
  avertissement?: string
  /** Une ligne neutre, quand il n'y a ni variation ni alerte à dire. */
  note?: string
  onClick: () => void
  onGraphe?: () => void
}): JSX.Element {
  return (
    <Paper
      sx={{
        position: 'relative',
        p: 4,
        borderRadius: 2,
        minWidth: 0,
        borderColor: (t) =>
          t.palette.mode === 'dark' ? palette.neutral[800] : palette.neutral[100],
        boxShadow: (t) =>
          t.palette.mode === 'dark'
            ? 'none'
            : '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
        '&:hover': { borderColor: 'primary.main' },
        '&:has(.carte-cible:focus-visible)': { outline: 2, outlineColor: 'primary.main' },
      }}
    >
      {/* La cible étendue : toute la carte, sous le bouton graphe. */}
      <ButtonBase
        className="carte-cible"
        onClick={onClick}
        aria-label={`${libelle} : ${valeur}${unite ? ` ${unite}` : ''}`}
        sx={{ position: 'absolute', inset: 0, borderRadius: 2, zIndex: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 600, flex: 1 }}
          noWrap
        >
          {libelle}
        </Typography>
        {onGraphe ? <BoutonGraphe libelle={libelle} onClick={onGraphe} /> : null}
      </Box>
      <Typography sx={{ fontSize: 26, lineHeight: '32px', fontWeight: 700 }} noWrap>
        {valeur}
        {unite ? (
          <Typography
            component="span"
            sx={{ fontSize: 13, ml: 1, color: 'text.secondary', fontWeight: 400 }}
          >
            {unite}
          </Typography>
        ) : null}
      </Typography>
      <Box sx={{ minHeight: 22, mt: 0.5 }}>
        {avertissement ? (
          <Typography variant="caption" sx={{ color: palette.warning[700], fontWeight: 600 }}>
            {avertissement}
          </Typography>
        ) : note ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {note}
          </Typography>
        ) : (
          <Variation
            valeur={variation ?? null}
            hausseFavorable={hausseFavorable}
            {...(libelleVariation ? { libelle: libelleVariation } : {})}
          />
        )}
      </Box>
      {tendance ? (
        <Box sx={{ mt: 2 }}>
          <Sparkline valeurs={tendance} titre={`Tendance : ${libelle}`} />
        </Box>
      ) : null}
    </Paper>
  )
}
